import {
  fetchBlocksByStudySessionId,
  fetchTodayStudySession,
  fetchVignettesForBlock,
} from '../services/study';
import {
  blocksCleared,
  blocksLoaded,
  selectActiveBlock,
  selectBlocks,
  selectNextActiveBlock,
} from '../slices/blocks/blocksSlice';
import { selectTodaySession } from '../slices/todaySession/todaySessionSlice';
import {
  fetchQbankProgress,
  updateStudySession,
} from '../services/studySession';
import { store } from '../store/store';
import { getBlockByIdForReview, updateBlock } from '../services/blocks';
import {
  selectStudyContext,
  setStandaloneBlockId,
  type StudyMode,
} from '../slices/studyContext/studyContextSlice';
import { capture } from '../utils/analytics';
import { setActiveQuestionIndex } from '../slices/study/studySlice';
import {
  clearVignettes,
  setVignettesForBlock,
} from '../slices/vignettes/vignettesSlice';
import {
  getQbankBlockById,
  normalizeQbankBlockResponse,
} from '../services/qbank';
import { getInstitutionAdminStudentQbankBlock } from '../services/institutionAdmin';

const hasLoadedVignettesForBlock = (blockId: string) => {
  const state = store.getState();

  const vignetteCount = Array.isArray(state.vignettes?.ids)
    ? state.vignettes.ids.length
    : 0;

  return vignetteCount > 0 && state.vignettes.blockId === blockId;
};

export const loadBlockStudy = async (
  blockId?: string | null,
  modeOverride?: StudyMode
) => {
  try {
    const studyContext = selectStudyContext(store.getState());

    const isStandalone =
      modeOverride === 'standalone' || studyContext.mode === 'standalone';

    if (isStandalone) {
      await loadStandaloneBlockStudy(blockId ?? studyContext.activeBlockId);
      return;
    }
    await fadeDelay();
    markTodaySessionAsStarted();
    await checkRefreshContent();
    let activeBlock = selectActiveBlock(store.getState());
    if (!activeBlock || activeBlock.isSubmitted) {
      await transitionToNextBlock();
      activeBlock = selectActiveBlock(store.getState());
    }
    if (activeBlock?.id && !hasLoadedVignettesForBlock(activeBlock.id)) {
      await fetchVignettesForBlock(activeBlock.id);
    }
    await markActiveBlockAsStarted();
  } catch (error) {
    console.error('Error loading block study:', error);
    throw error;
  }
};

export const loadBlockReview = async (
  blockId?: string | null,
  modeOverride?: StudyMode
) => {
  try {
    const studyContext = selectStudyContext(store.getState());

    const isStandalone =
      modeOverride === 'standalone' || studyContext.mode === 'standalone';

    if (isStandalone) {
      await loadStandaloneBlockReview(blockId ?? studyContext.activeBlockId);
      return;
    }
    if (!blockId) return;
    await fadeDelay();
    resetActiveQuestionIndex();
    const blocks = selectBlocks(store.getState());
    const isCurrentSessionBlock = blocks.some((b) => b.id === blockId);

    if (isCurrentSessionBlock) {
      const todaySession = selectTodaySession(store.getState());

      await Promise.all([
        updateStudySession({
          id: todaySession.id,
          activeBlockId: blockId,
        }),
        fetchVignettesForBlock(blockId),
      ]);
    } else {
      await getBlockByIdForReview(blockId);
    }
  } catch (error) {
    console.error('Error loading block review:', error);
    throw error;
  }
};

export const loadFlashcardStudy = async () => {
  try {
    await fadeDelay();
    markTodaySessionAsStarted();
    await checkRefreshContent();
  } catch (error) {
    console.error('Error loading flashcard study:', error);
    throw error;
  }
};

export const loadSessionSummary = async () => {
  try {
    await fadeDelay();

    const studySession = await forceRefreshBlocksAndVignettes();
    if (studySession?.id) {
      await fetchQbankProgress(studySession.id);
    }
    await markTodaySessionAsComplete();
  } catch (error) {
    console.error('Error loading session summary:', error);
    throw error;
  }
};

const loadStandaloneBlockStudy = async (blockId?: string | null) => {
  if (!blockId) {
    throw new Error('Missing block ID for standalone study.');
  }
  await fadeDelay();
  resetActiveQuestionIndex();
  store.dispatch(blocksCleared());
  store.dispatch(clearVignettes());
  const response = await getQbankBlockById(blockId);
  const normalized = normalizeQbankBlockResponse(response);
  const block = normalized?.block;
  if (!block) {
    throw new Error('Failed to load standalone block.');
  }
  store.dispatch(setStandaloneBlockId(block.id));
  store.dispatch(blocksLoaded([block]));
  if (normalized?.vignettes?.length) {
    store.dispatch(
      setVignettesForBlock({
        blockId: block.id,
        vignettes: normalized.vignettes,
      })
    );
  } else {
    await fetchVignettesForBlock(block.id);
  }
  await markActiveBlockAsStarted();
};

const loadStandaloneBlockReview = async (blockId?: string | null) => {
  if (!blockId) {
    throw new Error('Missing block ID for standalone review.');
  }
  await fadeDelay();
  resetActiveQuestionIndex();
  store.dispatch(blocksCleared());
  store.dispatch(clearVignettes());

  // When a faculty admin is reviewing a student's block, the block is owned by
  // the student, so load it through the faculty-scoped endpoint (resolved from
  // the roster context carried on the study context).
  const facultyReviewTarget = selectStudyContext(
    store.getState()
  ).facultyReviewTarget;

  const response = facultyReviewTarget
    ? await getInstitutionAdminStudentQbankBlock(
        facultyReviewTarget.cohortId,
        facultyReviewTarget.rosterEntryId,
        blockId
      )
    : await getQbankBlockById(blockId);

  const normalized = normalizeQbankBlockResponse(response);
  const block = normalized?.block;
  if (!block) {
    throw new Error('Failed to load standalone block.');
  }
  store.dispatch(setStandaloneBlockId(block.id));
  store.dispatch(blocksLoaded([block]));
  if (normalized?.vignettes?.length) {
    store.dispatch(
      setVignettesForBlock({
        blockId: block.id,
        vignettes: normalized.vignettes,
      })
    );
  } else {
    await fetchVignettesForBlock(block.id);
  }
};

const markTodaySessionAsComplete = async () => {
  const todaySession = selectTodaySession(store.getState());
  if (todaySession.id && !todaySession.isComplete) {
    capture(
      'study_session_complete',
      { studySessionId: todaySession.id },
      { withExam: true }
    );

    await updateStudySession({
      id: todaySession.id,
      isComplete: true,
    });
  }
};

const markActiveBlockAsStarted = async () => {
  const state = store.getState();
  const activeBlock = selectActiveBlock(state);
  if (activeBlock?.id && !activeBlock.isStarted) {
    const todaySession = selectTodaySession(state);

    // Typed start
    capture(
      activeBlock.type + '_block_start',
      {
        studySessionId: todaySession?.id,
        blockId: activeBlock.id,
        blockType: activeBlock.type,
      },
      { withExam: true }
    );

    // Generic start
    capture(
      'block_start',
      {
        studySessionId: todaySession?.id,
        blockId: activeBlock.id,
        blockType: activeBlock.type,
      },
      { withExam: true }
    );

    await updateBlock({
      blockId: activeBlock.id,
      isStarted: true,
    });

    resetActiveQuestionIndex();
  }
};

const transitionToNextBlock = async () => {
  const state = store.getState();
  const todaySession = selectTodaySession(state);
  const activeBlock = selectActiveBlock(state);
  const nextBlock = selectNextActiveBlock(state);

  if (nextBlock && nextBlock.id !== activeBlock?.id && todaySession?.id) {
    store.dispatch(clearVignettes());

    await Promise.all([
      updateStudySession({
        id: todaySession.id,
        activeBlockId: nextBlock.id,
      }),
      fetchVignettesForBlock(nextBlock.id),
    ]);
  }
};

export const forceRefreshBlocksAndVignettes = async () => {
  const studySession = await fetchTodayStudySession();
  await fetchBlocksByStudySessionId(studySession.id);
  const activeBlockId = studySession.activeBlockId;
  if (activeBlockId) {
    await fetchVignettesForBlock(activeBlockId);
  }
  return studySession;
};

const checkRefreshContent = async () => {
  let todaySession = selectTodaySession(store.getState());
  if (!todaySession.id) {
    await fetchTodayStudySession();
    todaySession = selectTodaySession(store.getState());
  }
  if (!todaySession.id) {
    return;
  }
  if (todaySession.isUsingQbank) {
    const activeBlock = selectActiveBlock(store.getState());
    if (!activeBlock) {
      await fetchBlocksByStudySessionId(todaySession.id);
      await transitionToNextBlock();
    }
  }
  if (todaySession.isUsingFlashcards) {
    return;
  }
};

const markTodaySessionAsStarted = async () => {
  const todaySession = selectTodaySession(store.getState());
  if (todaySession && !todaySession.isStarted) {
    capture(
      'study_session_start',
      { studySessionId: todaySession.id },
      { withExam: true }
    );

    capture(
      'daily_study_session_start',
      { studySessionId: todaySession.id },
      { withExam: true }
    );

    await updateStudySession({
      id: todaySession.id,
      isStarted: true,
    });
  }
};

export const resetActiveQuestionIndex = () => {
  store.dispatch(setActiveQuestionIndex(0));
};

// Add small delay to allow for smooth fading transitions
const fadeDelay = async (ms: number = 250) => {
  await new Promise((resolve) => setTimeout(resolve, ms));
};
